import { useState, useEffect } from 'react';
import { Eye, EyeOff, Loader2, Lock, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '../../services/supabaseClient';
import { validatePassword, PASSWORD_RULES, PASSWORD_MIN_LENGTH } from '../../utils/passwordValidation';
import { sanitizeError } from '../../utils/errorMessages';
import { useCopy, fill } from '../../i18n';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

const ResetPasswordScreen = () => {
  const copy = useCopy();
  const t = copy.auth;
  // As regras de senha devolvem chave, não texto: o rótulo vem do dicionário.
  const ruleLabel = (key: keyof typeof copy.auth.passwordRules) =>
    fill(copy.auth.passwordRules[key], { min: PASSWORD_MIN_LENGTH });
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);
  const [linkExpired, setLinkExpired] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);

  // Aguardar sessão ser estabelecida a partir dos tokens da URL
  useEffect(() => {
    // Verificar se já há erro na URL (link expirado)
    const url = new URL(window.location.href);
    const error = url.searchParams.get('error');
    const errorCode = url.searchParams.get('error_code');

    if (error === 'access_denied' || errorCode === 'otp_expired') {
      setLinkExpired(true);
      return;
    }

    // Listener para mudanças de autenticação
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[ResetPassword] Auth event:', event, 'Session:', !!session);

      if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) {
        console.log('[ResetPassword] Sessão estabelecida, formulário habilitado');
        setSessionReady(true);
      }
    });

    // Verificar se já existe uma sessão (caso o token já tenha sido processado)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        console.log('[ResetPassword] Sessão já existe');
        setSessionReady(true);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');

    const { valid, failures } = validatePassword(password);
    if (!valid) {
      setMessage(fill(copy.auth.validation.weakPassword, { failures: failures.map(ruleLabel).join(', ') }));
      return;
    }

    if (password !== confirmPassword) {
      setMessage(t.validation.passwordMismatch);
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) throw error;

      // SignOut para limpar sessão e evitar eventos de auth interferindo
      await supabase.auth.signOut();

      setSuccess(true);
    } catch (err: unknown) {
      console.error('[ResetPassword] Error:', err);

      // Mostrar mensagem amigável baseada no erro
      const errMsg = err instanceof Error ? err.message : '';
      if (errMsg.includes('session') || errMsg.includes('token') || errMsg.includes('JWT')) {
        setMessage(t.errors.linkExpiredLong);
      } else if (errMsg.includes('same_password') || errMsg.includes('different from the old')) {
        setMessage(t.errors.samePassword);
      } else {
        setMessage(sanitizeError(err));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoToLogin = () => {
    window.location.href = '/';
  };

  // Tela de link expirado
  if (linkExpired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[linear-gradient(135deg,#1a1a2e_0%,#2d1b69_50%,var(--color-primary)_100%)] p-5">
        <Card className="w-full max-w-[400px] shadow-[0_20px_60px_rgba(0,0,0,0.3)] text-center">
          <CardContent className="p-10">
            <div className="flex justify-center mb-6" style={{ color: 'var(--color-danger)' }}>
              <AlertCircle size={64} />
            </div>
            <h2 className="text-[var(--font-2xl)] font-bold text-[var(--color-text)] m-0 mb-4">{t.linkExpired.title}</h2>
            <p className="text-base text-[var(--color-gray-500)] m-0 mb-2">
              {t.linkExpired.body}
            </p>
            <p className="text-[var(--font-base)] text-[var(--color-gray-400)] m-0 mb-8 leading-normal">
              {t.linkExpired.hint}
            </p>
            <Button variant="primary" size="xl" className="w-full" onClick={handleGoToLogin}>
              {t.switch.backToLogin}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Tela de sucesso
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[linear-gradient(135deg,#1a1a2e_0%,#2d1b69_50%,var(--color-primary)_100%)] p-5">
        <Card className="w-full max-w-[400px] shadow-[0_20px_60px_rgba(0,0,0,0.3)] text-center">
          <CardContent className="p-10">
            <div className="flex justify-center mb-6 [&_svg]:text-[var(--color-success)]">
              <CheckCircle size={64} />
            </div>
            <h2 className="text-[var(--font-2xl)] font-bold text-[var(--color-text)] m-0 mb-4">{t.resetDone.title}</h2>
            <p className="text-base text-[var(--color-gray-500)] m-0 mb-2">
              {t.resetDone.body}
            </p>
            <Button variant="primary" size="xl" className="w-full" onClick={handleGoToLogin}>
              {t.resetDone.action}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[linear-gradient(135deg,#1a1a2e_0%,#2d1b69_50%,var(--color-primary)_100%)] p-5">
      {/* Inline styles for autofill and focus that cannot be done with Tailwind */}
      <style>{`
        .reset-input:-webkit-autofill,
        .reset-input:-webkit-autofill:hover,
        .reset-input:-webkit-autofill:focus {
          -webkit-box-shadow: 0 0 0 1000px #fff inset !important;
          -webkit-text-fill-color: #1a1a2e !important;
          box-shadow: 0 0 0 1000px #fff inset !important;
        }
        .reset-input:focus {
          outline: none;
          border-color: var(--color-primary);
          box-shadow: 0 0 0 3px rgba(126, 62, 238, 0.15);
        }
      `}</style>
      <Card className="w-full max-w-[400px] shadow-[0_20px_60px_rgba(0,0,0,0.3)]">
        <CardContent className="p-10">
        <div className="flex items-center justify-center gap-3 mb-8">
          <img src="/logo.svg" alt="MeuGantt" className="w-12 h-12" />
          <h1 className="text-[var(--font-2xl)] font-bold text-[var(--color-primary)] m-0">MeuGantt</h1>
        </div>

        <h2 className="text-center text-[var(--font-xl)] font-semibold text-[var(--color-gray-700)] m-0 mb-6">{t.title.resetPassword}</h2>

        <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
          <div className="flex flex-col gap-2">
            <Label htmlFor="password" className="text-[var(--font-base)] font-medium text-[var(--color-gray-700)]">{t.field.newPassword}</Label>
            <div className="relative flex items-center">
              <Lock size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--color-gray-400)] pointer-events-none z-[1]" />
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t.field.newPasswordPlaceholder}
                required
                minLength={PASSWORD_MIN_LENGTH}
                className="reset-input w-full py-3 pl-11 pr-11 border-2 border-[var(--color-gray-200)] rounded-[var(--radius-lg)] text-base !bg-white !text-[#1a1a2e] transition-[border-color,box-shadow] duration-200 box-border"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 bg-transparent border-none text-[var(--color-gray-500)] cursor-pointer p-1 flex items-center justify-center hover:text-[var(--color-gray-700)]"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <div className="flex flex-col gap-0.5 mt-1.5">
              {PASSWORD_RULES.map((rule) => {
                const passed = password && rule.test(password);
                return (
                  <span
                    key={rule.key}
                    className={cn("text-xs transition-colors duration-200", passed ? "text-[var(--color-success)]" : "text-[var(--color-gray-500)]")}
                  >
                    {passed ? '✓' : '○'} {ruleLabel(rule.key)}
                  </span>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="confirmPassword" className="text-[var(--font-base)] font-medium text-[var(--color-gray-700)]">{t.field.confirmPassword}</Label>
            <div className="relative flex items-center">
              <Lock size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--color-gray-400)] pointer-events-none z-[1]" />
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t.field.confirmPasswordPlaceholder}
                required
                minLength={PASSWORD_MIN_LENGTH}
                className="reset-input w-full py-3 pl-11 pr-11 border-2 border-[var(--color-gray-200)] rounded-[var(--radius-lg)] text-base !bg-white !text-[#1a1a2e] transition-[border-color,box-shadow] duration-200 box-border"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 bg-transparent border-none text-[var(--color-gray-500)] cursor-pointer p-1 flex items-center justify-center hover:text-[var(--color-gray-700)]"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {message && (
            <div className="py-3 px-4 rounded-[var(--radius-lg)] text-[var(--font-base)] text-center bg-[var(--color-danger-bg)] text-[var(--color-danger-dark)] border border-[var(--color-danger-border)]">
              {message}
            </div>
          )}

          <Button type="submit" variant="primary" size="xl" className="w-full" disabled={isSubmitting || !sessionReady}>
            {!sessionReady ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                {t.loading.preparing}
              </>
            ) : isSubmitting ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                {t.loading.updating}
              </>
            ) : (
              t.action.updatePassword
            )}
          </Button>
        </form>

        <div className="flex flex-col items-center gap-3 mt-6 pt-6 border-t border-[var(--color-gray-200)]">
          <Button
            type="button"
            variant="link"
            className="text-[var(--color-gray-500)] hover:text-[var(--color-primary)]"
            onClick={handleGoToLogin}
          >
            {t.switch.backToLogin}
          </Button>
        </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPasswordScreen;
